import { apiBaseUrl, authCacheScope } from "@rekonas/data-access";

const CHUNK_BYTES = 8 * 1024 * 1024;

function read(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error("Could not read this archive. Choose it again."));
    reader.readAsArrayBuffer(blob);
  });
}

/** Hash every byte with bounded memory. This is a versioned local identity,
 * not the canonical raw archive SHA-256 used by server acceptance. */
export async function archiveIdentity(file: File): Promise<string> {
  const digests: Uint8Array[] = [];
  for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
    digests.push(new Uint8Array(await crypto.subtle.digest("SHA-256",
      await read(file.slice(offset, offset + CHUNK_BYTES)))));
  }
  const hashes = new Uint8Array(digests.length * 32);
  digests.forEach((digest, index) => hashes.set(digest, index * 32));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", hashes));
  return `chunks-v1-${file.size}-${Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export function identityStorageKey(file: File, identity: string): string {
  return JSON.stringify(["rekonas-operational-upload-v2", apiBaseUrl, authCacheScope(), file.name, identity]);
}

export function durableIdempotencyKey(file: File, identity: string, fresh = false): string {
  const storageKey = identityStorageKey(file, identity);
  const retained = window.localStorage.getItem(storageKey);
  if (retained && !fresh) return retained;
  // Older keys did not prove byte identity or isolate accounts. Never silently
  // adopt their partial bytes or allocate a duplicate acquisition beside them.
  if (!fresh && window.localStorage.getItem(`rekonas-operational-upload:${file.name}:${file.size}:${file.lastModified}`)) {
    throw new Error("An older browser upload exists for this archive. Ask an operator to check its status before starting a fresh upload.");
  }
  const value = `web-${crypto.randomUUID()}`;
  window.localStorage.setItem(storageKey, value);
  return value;
}
